import {
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DynamicSuspenseContainer from 'src/components/DynamicSuspenseContainer/DynamicSuspenseContainer'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingShimmer, {
  LoadingShimmerType,
} from 'src/components/LoadingShimmer/LoadingShimmer'
import { TransactionsFiltersBody } from 'src/components/TransactionsListFilter/transactionsFilter.schema'
import Trns from 'src/localization/Trns'
import { useSuspenseQueryTransactionsCount } from 'src/queries/transactions.queries'
import {
  updateSelectAllTransactionsBoxVisible,
  updateSelectAllTransactionsFilters,
  updateSelectedTransactions,
} from 'src/redux/actions/data/transactions.actions'
import { selectIsSelectAllTransactionsFromState } from 'src/redux/selectors/data/transactions.selectors'
import { css } from 'styled-components'

interface SelectAllTransactionsBoxProps {
  filtersBody: TransactionsFiltersBody | undefined
  selectedTransactionCount: number
}

const SelectAllTransactionsBox: React.ForwardRefRenderFunction<
  HTMLDivElement,
  SelectAllTransactionsBoxProps
> = (props, ref) => {
  return (
    <div
      ref={ref}
      css={css`
        display: flex;
        gap: 8px;
        padding: 16px 20px;
        margin: 20px 24px 0;
        background-color: ${styleConstants.colors.gray_light};
        border: 1px solid ${styleConstants.colors.gray_medium};
        border-radius: 8px;
      `}
    >
      <DynamicSuspenseContainer
        fallback={
          <LoadingShimmer
            width={'25%'}
            height={'16px'}
            type={LoadingShimmerType.OnGrayLight}
          />
        }
      >
        <SelectAllTransactionsBoxContent {...props} />
      </DynamicSuspenseContainer>
    </div>
  )
}

const CountText: React.FC<{ count: number; allSelected: boolean }> = ({
  count,
  allSelected,
}) => {
  return (
    <span
      css={css`
        ${getFontCss(
          12,
          themeConstants.colors.font_primary,
          styleConstants.fontFamilies.regular,
        )};
      `}
    >
      <Trns
        source={
          allSelected
            ? 'All ${count} transactions are selected.'
            : '${count} transactions on this page are selected.'
        }
        expressions={{
          count: (
            <span
              css={css`
                ${getFontCss(
                  12,
                  themeConstants.colors.font_primary,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {count}
            </span>
          ),
        }}
      />
    </span>
  )
}

const SelectAllLinkText: React.FC<{
  count: number | undefined
  onClick: () => void
}> = ({ count, onClick }) => {
  const textSource =
    count !== undefined
      ? 'Select all ${count} transactions (based on selected filters)'
      : 'Clear selection'
  return (
    <HoverableLink
      size={HoverableLinkSizes.Small}
      onClick={onClick}
      ariaLabelSource={textSource}
    >
      <span>
        <Trns
          source={textSource}
          expressions={
            count !== undefined ? { count: <span>{count}</span> } : undefined
          }
        />
      </span>
    </HoverableLink>
  )
}

const SelectAllTransactionsBoxContent: React.FC<
  SelectAllTransactionsBoxProps
> = (props) => {
  const dispatch = useDispatch()

  const { data: selectAllTransactionsCount } =
    useSuspenseQueryTransactionsCount({ filters: props.filtersBody })
  const allSelected = useSelector(selectIsSelectAllTransactionsFromState)

  // clean up and make sure redux state for select all does not linger
  useEffect(() => {
    return () => {
      dispatch(updateSelectAllTransactionsFilters(undefined))
      dispatch(updateSelectAllTransactionsBoxVisible(false))
    }
  }, [])

  return (
    <>
      <CountText
        count={
          allSelected
            ? selectAllTransactionsCount
            : props.selectedTransactionCount
        }
        allSelected={allSelected}
      />
      <SelectAllLinkText
        count={!allSelected ? selectAllTransactionsCount : undefined}
        onClick={(): void => {
          if (allSelected) {
            dispatch(updateSelectAllTransactionsBoxVisible(false))
            dispatch(updateSelectedTransactions())
          } else {
            dispatch(updateSelectAllTransactionsFilters(props.filtersBody))
          }
        }}
      />
    </>
  )
}

export default React.forwardRef(SelectAllTransactionsBox)
